import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response as FetchResponse } from "node-fetch";
import fetch from "node-fetch";
import { Response } from "playwright-core";
import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { SteelAction } from "./index";

const scanRetryMaxCount = 60; // 二维码扫描重试次数
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000; // 最长等待时间

export const rpaServer: SteelAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.localIP) {
        const args: [string, string | undefined | null, number | undefined] = [
            params.localIP,
            params.proxyLoc,
            params.accountId,
        ];
        task.logger?.info(`==> 开始获取代理信息：${args}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, ...args);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    const page = await task.createPage({
        url: "https://passport.baidu.com/v2/?login",
        proxyUrl,
        userAgent: params.userAgent,
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });

    task.steelBrowser?.on("disconnected", async () => {
        const browserContext = task.steelBrowserContext;
        const browser = task.steelBrowser;
        executionState = ExecutionState.BROWSER_CLOSED;
        await page.close();

        if (browserContext) {
            await browserContext.close();
        }
        if (browser) {
            await browser.close();
        }
    });

    // 删除 TANGRAM__PSP_3__QrcodeAnimation 元素
    await page.locator("#TANGRAM__PSP_3__QrcodeAnimation").evaluate((el) => el.remove());

    // 修改 TANGRAM__PSP_3__QrcodeMain 的 style
    await page.locator("#TANGRAM__PSP_3__QrcodeMain").evaluate((el) => {
        // 先获取现有 style
        const style = el.getAttribute("style") || "";
        // 用正则去掉 margin-left: 39px;
        const newStyle = style.replace(/margin-left:\s*39px;?/i, "");
        el.setAttribute("style", newStyle);
    });

    await page.locator(".tang-pass-qrcode-title a.pass-link").evaluate((el) => {
        el.textContent = "百家号APP";
    });

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: await task.steelConnector?.getLive(task.sessionId || ""),
        sessionId: task.sessionId,
    });

    let token: string | undefined;
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            const timer = setTimeout(async () => {
                cleanup();
                executionState = ExecutionState.TIMEOUT;
                await updateTaskState?.({
                    state: TaskState.TIMEOUT,
                    error: "等待扫码登录超时",
                });
                resolve();
            }, waitQrcodeResultMaxTime);

            const cleanup = () => {
                if (finished) return; // 防止多次调用
                finished = true;
                clearTimeout(timer);
                page.off("response", handleWebCommon);
                page.off("response", handleCheckQR);
                task.steelBrowser?.off("disconnected", handleBrowserClosed);
            };

            const handleBrowserClosed = () => {
                cleanup();
                executionState = ExecutionState.BROWSER_CLOSED;
                resolve();
            };

            const handleWebCommon = async (resp: Response) => {
                try {
                    const url = resp.url();

                    // 获取用户信息和 token
                    if (url.includes("/user-ui/cms/getPassinfo") || url.includes("/pcui/home/dealtoken")) {
                        try {
                            const headers = await resp.request().allHeaders();
                            let res: FetchResponse;

                            for (let i = 0; i < 3; i++) {
                                try {
                                    res = await fetch("https://baijiahao.baidu.com/user-ui/cms/getPassinfo", {
                                        method: "GET",
                                        headers: {
                                            cookie: headers.cookie,
                                        },
                                    });
                                    if (res.ok) {
                                        token = res.headers.get("token") || "";
                                        const jsonUser = (await res.json()) as {
                                            data: { avatar: string; uk: string; displayname: string };
                                        };
                                        userInfo = {
                                            name: jsonUser.data.displayname ?? "",
                                            avatar: jsonUser.data.avatar ?? "",
                                            uniqueId: jsonUser.data.uk ?? "",
                                        };
                                        break;
                                    }
                                    await new Promise((res) => setTimeout(res, 500));
                                } catch (error) {
                                    if (i === 2) {
                                        task.logger.warn("获取用户信息失败:", { error });
                                    }
                                    await new Promise((res) => setTimeout(res, 500));
                                }
                            }
                        } catch (e) {
                            task.logger.warn("处理用户信息失败:", { url, error: e });
                        }
                    }

                    // 登录成功
                    if (url.includes("/pcui/home/dealtoken")) {
                        cleanup();
                        await updateTaskState?.({
                            state: TaskState.CONFIRMED,
                        });

                        executionState = ExecutionState.SUCCESS;
                        resolve();
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            const handleCheckQR = async (response: Response) => {
                try {
                    const url = response.url();
                    if (!url.includes("/channel/unicast")) return;

                    let jsonResponse: { errno: number; channel_v?: string };
                    try {
                        const jsonPStr = await response.text();
                        if (!jsonPStr) {
                            task.logger.warn("Empty response body:", { url });
                            return;
                        }

                        const jsonStr = jsonPStr.replace(/^[^(]+\(([\s\S]*)\)\s*$/, "$1");
                        jsonResponse = JSON.parse(jsonStr);
                    } catch (e) {
                        task.logger.warn("Failed to parse json:", { url, error: e });
                        return;
                    }

                    if (jsonResponse.errno === 0 && jsonResponse?.channel_v) {
                        const channel_v: { status: number } = JSON.parse(jsonResponse.channel_v);
                        if (channel_v.status === 1) {
                            // 扫码成功
                            await updateTaskState?.({
                                state: TaskState.SCANNED,
                            });
                        }
                        if (channel_v.status === 0) {
                            page.off("response", handleCheckQR);
                            await page.goto("https://baijiahao.baidu.com/builder/rc/home");
                            page.on("response", handleWebCommon);
                        }
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 在页面加载之前就注册监听，防止丢失请求
            task.steelBrowser?.on("disconnected", handleBrowserClosed);
            page.on("response", handleCheckQR);
        });
    } finally {
        await page.close();
    }

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS: {
            // 直接序列化原始 cookie 数组，切勿对 JSON 字符串做 replace(/\\"/g, "")：
            // 部分百度 cookie（如 RT）的 value 自身包含双引号/反斜杠，
            // 删除转义符会破坏 JSON 结构，parse 后产生 name:"" value:"undefined" 的脏数据。
            const cookie = JSON.stringify(await task.steelBrowserContext?.cookies());
            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie,
                    token,
                    userInfo: userInfo,
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie,
                    token,
                    userInfo,
                },
            };
        }
        default:
            // 处理 RUNNING 和其他可能的状态
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
